import type { Harvest } from '../types';
import * as harvestApi from '../api/harvest';
import { KeyedAsyncCache } from './asyncCache';

const STATUS_FROM_BACKEND: Record<harvestApi.BackendHarvestStatus, Harvest['status']> = {
  EXPECTED: 'Dự kiến',
  FARMER_UPDATED: 'Nông hộ đã cập nhật',
  PENDING_HTX_CONFIRM: 'Chờ HTX xác nhận',
  HTX_CONFIRMED: 'HTX đã xác nhận',
  LOW_YIELD: 'Sản lượng thấp',
  COMPLETED: 'Hoàn thành',
  CANCELLED: 'Hủy',
};

const QUALITY_FROM_BACKEND: Record<harvestApi.BackendHarvestQuality, string> = {
  GOOD: 'Tốt',
  AVERAGE: 'Trung bình',
  POOR: 'Kém',
};

function toFrontendHarvest(h: harvestApi.BackendHarvest): Harvest {
  return {
    id: h._id,
    maLanThuHoach: h.code || `TH-${h._id.slice(-6)}`,
    farmerId: h.farmerId,
    fieldId: h.fieldId,
    seasonId: h.seasonId,
    contractId: h.contractId,
    cayTrong: h.mainCrop,
    loaiSanPham: h.productType,
    soLuong: h.expectedQuantity,
    actualYield: h.actualQuantity,
    actualYieldConfirmed: h.actualQuantityConfirmed ? h.actualQuantity : undefined,
    donVi: h.unit,
    ngayDuKien: h.expectedDate,
    ngayThuHoach: h.harvestedDate,
    status: STATUS_FROM_BACKEND[h.status] ?? 'Dự kiến',
    giaUocTinh: h.estimatedPriceVnd,
    chatLuong: h.quality ? QUALITY_FROM_BACKEND[h.quality] : undefined,
    ghiChuChatLuong: h.qualityNote,
    ghiChu: h.notes,
    canhBao: h.warning,
    lichSuCapNhat: h.updateLog.map((entry) => ({ thoiGian: entry.at, nguoiCapNhat: entry.by, noiDung: entry.note })),
    isDeleted: h.isDeleted,
  };
}

const byFarmer = new KeyedAsyncCache<Harvest[]>(
  async (farmerId) => (await harvestApi.listHarvestsByFarmer(farmerId)).map(toFrontendHarvest),
  [],
  'harvests:byFarmer',
);
const byHtx = new KeyedAsyncCache<Harvest[]>(
  async (htxId) => (await harvestApi.listHarvestsByHtx(htxId)).map(toFrontendHarvest),
  [],
  'harvests:byHtx',
);

export function getHarvestsByFarmer(farmerId?: string): Harvest[] {
  if (!farmerId) return [];
  return byFarmer.get(farmerId);
}

export function getHarvestsByHtx(htxId?: string): Harvest[] {
  if (!htxId) return [];
  return byHtx.get(htxId);
}

function refresh(farmerId: string, htxId?: string) {
  byFarmer.invalidate(farmerId);
  byFarmer.get(farmerId);
  if (htxId) {
    byHtx.invalidate(htxId);
    byHtx.get(htxId);
  }
}

export async function addHarvest(data: Omit<Harvest, 'id' | 'maLanThuHoach'>, htxId?: string): Promise<Harvest> {
  const created = await harvestApi.createHarvest({
    farmerId: data.farmerId,
    fieldId: data.fieldId,
    seasonId: data.seasonId,
    contractId: data.contractId,
    mainCrop: data.cayTrong,
    productType: data.loaiSanPham,
    expectedQuantity: data.soLuong,
    unit: data.donVi,
    expectedDate: data.ngayDuKien,
    estimatedPriceVnd: data.giaUocTinh,
    notes: data.ghiChu,
  });
  refresh(data.farmerId, htxId);
  return toFrontendHarvest(created);
}

export async function reportHarvestActual(id: string, farmerId: string, actualQuantity: number, harvestedDate?: string, notes?: string, htxId?: string): Promise<Harvest> {
  const updated = await harvestApi.reportActualHarvest(id, { actualQuantity, harvestedDate, notes });
  refresh(farmerId, htxId);
  return toFrontendHarvest(updated);
}

export async function confirmHarvest(id: string, farmerId: string, htxId: string, quality?: string, qualityNote?: string): Promise<Harvest> {
  const backendQuality = (Object.entries(QUALITY_FROM_BACKEND).find(([, label]) => label === quality)?.[0] as harvestApi.BackendHarvestQuality) || undefined;
  const updated = await harvestApi.confirmHarvest(id, { quality: backendQuality, qualityNote });
  refresh(farmerId, htxId);
  return toFrontendHarvest(updated);
}

export async function completeHarvest(id: string, farmerId: string, htxId: string): Promise<Harvest> {
  const updated = await harvestApi.completeHarvest(id);
  refresh(farmerId, htxId);
  return toFrontendHarvest(updated);
}

export async function cancelHarvest(id: string, farmerId: string, reason: string, htxId?: string): Promise<Harvest> {
  const updated = await harvestApi.cancelHarvest(id, reason);
  refresh(farmerId, htxId);
  return toFrontendHarvest(updated);
}
